/**
 * Finding images that actually display. A stored image name says nothing about whether the file
 * is still on the image host, and the only reliable test is the one the browser makes when it
 * shows it — so candidates are loaded quietly and only those that load are kept.
 */

/** Resolves the first URL in `urls` that loads as an image, or null when none does. */
export function firstLoadingImage(urls: readonly string[]): Promise<string | null> {
  return new Promise((resolve) => {
    let i = 0;
    const next = () => {
      if (i >= urls.length) { resolve(null); return; }
      const url = urls[i++]!;
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = next;
      img.src = url;
    };
    next();
  });
}

/**
 * Walks `candidates` in order, `batchSize` at a time, and keeps the first `want` for which
 * `probe` finds a URL — each paired with that URL, so the tile shows it without trying again.
 * `onProgress` gets the list so far after every batch that added something; `isStale` stops the
 * walk when the caller has moved on (a newer search), and its result is then ignored.
 */
export async function pickWithPreview<T>(
  candidates: readonly T[],
  probe: (item: T) => Promise<string | null>,
  want: number,
  options: { batchSize?: number; isStale?: () => boolean; onProgress?: (found: { item: T; src: string }[]) => void } = {},
): Promise<{ item: T; src: string }[]> {
  const { batchSize = 10, isStale = () => false, onProgress } = options;
  const found: { item: T; src: string }[] = [];
  for (let i = 0; i < candidates.length && found.length < want; i += batchSize) {
    const batch = candidates.slice(i, i + batchSize);
    const srcs = await Promise.all(batch.map(probe));
    if (isStale()) return found;
    const before = found.length;
    for (let j = 0; j < batch.length && found.length < want; j++) {
      const src = srcs[j];
      if (src) found.push({ item: batch[j]!, src });
    }
    if (found.length > before) onProgress?.([...found]);
  }
  return found;
}
